import dayjs from "lib/dayjs";
import { dayCountFraction, daysBetween } from "lib/dayCount";

export const US_TREASURY_METHOD = "ust";

export const US_TREASURY_CURVE_NODES = Object.freeze([
  { securityId: "ust-1m", tenor: "1M", amount: 1, unit: "month" },
  { securityId: "ust-6w", tenor: "6W", amount: 6, unit: "week" },
  { securityId: "ust-2m", tenor: "2M", amount: 2, unit: "month" },
  { securityId: "ust-3m", tenor: "3M", amount: 3, unit: "month" },
  { securityId: "ust-4m", tenor: "4M", amount: 4, unit: "month" },
  { securityId: "ust-6m", tenor: "6M", amount: 6, unit: "month" },
  { securityId: "ust-1y", tenor: "1Y", amount: 1, unit: "year" },
  { securityId: "ust-2y", tenor: "2Y", amount: 2, unit: "year" },
  { securityId: "ust-3y", tenor: "3Y", amount: 3, unit: "year" },
  { securityId: "ust-5y", tenor: "5Y", amount: 5, unit: "year" },
  { securityId: "ust-7y", tenor: "7Y", amount: 7, unit: "year" },
  { securityId: "ust-10y", tenor: "10Y", amount: 10, unit: "year" },
  { securityId: "ust-20y", tenor: "20Y", amount: 20, unit: "year" },
  { securityId: "ust-30y", tenor: "30Y", amount: 30, unit: "year" },
]);

export const US_TREASURY_SECURITY_IDS = Object.freeze(
  US_TREASURY_CURVE_NODES.map((node) => node.securityId)
);

export const US_AGENCY_METHOD = "agency";
export const US_AGENCY_CURVE_NODES = Object.freeze([
  { securityId: "us-agcy-3m", tenor: "3M", amount: 3, unit: "month" },
  { securityId: "us-agcy-6m", tenor: "6M", amount: 6, unit: "month" },
  { securityId: "us-agcy-1y", tenor: "1Y", amount: 1, unit: "year" },
  { securityId: "us-agcy-2y", tenor: "2Y", amount: 2, unit: "year" },
  { securityId: "us-agcy-3y", tenor: "3Y", amount: 3, unit: "year" },
  { securityId: "us-agcy-4y", tenor: "4Y", amount: 4, unit: "year" },
  { securityId: "us-agcy-5y", tenor: "5Y", amount: 5, unit: "year" },
  { securityId: "us-agcy-7y", tenor: "7Y", amount: 7, unit: "year" },
  { securityId: "us-agcy-8y", tenor: "8Y", amount: 8, unit: "year" },
  { securityId: "us-agcy-9y", tenor: "9Y", amount: 9, unit: "year" },
  { securityId: "us-agcy-10y", tenor: "10Y", amount: 10, unit: "year" },
  { securityId: "us-agcy-15y", tenor: "15Y", amount: 15, unit: "year" },
  { securityId: "us-agcy-20y", tenor: "20Y", amount: 20, unit: "year" },
  { securityId: "us-agcy-25y", tenor: "25Y", amount: 25, unit: "year" },
  { securityId: "us-agcy-30y", tenor: "30Y", amount: 30, unit: "year" },
]);
export const US_AGENCY_SECURITY_IDS = Object.freeze(
  US_AGENCY_CURVE_NODES.map((node) => node.securityId)
);

export const HYPOTHETICAL_CURVE_UNIVERSES = Object.freeze({
  ust: Object.freeze({
    key: "ust",
    methodology: "us-treasury",
    universeLabel: "US Treasury",
    securityLabel: "U.S. Treasury",
    curveLabel: "US Treasury",
    methodologyNote: "Assumes hypothetical U.S. Treasury securities are available with maturities matching each required cash flow date and yields determined by interpolation of the selected U.S. Treasury yield curve.",
    resultKey: "usTreasury",
    nodes: US_TREASURY_CURVE_NODES,
    securityIds: US_TREASURY_SECURITY_IDS,
  }),
  agency: Object.freeze({
    key: "agency",
    methodology: "us-agencies",
    universeLabel: "US Agencies",
    securityLabel: "U.S. Agency",
    curveLabel: "US Agencies",
    methodologyNote: "Assumes hypothetical U.S. Agency securities are available with maturities matching each required cash flow date and yields determined by interpolation of the selected U.S. Agencies yield curve.",
    resultKey: "usAgency",
    nodes: US_AGENCY_CURVE_NODES,
    securityIds: US_AGENCY_SECURITY_IDS,
  }),
  custom: Object.freeze({
    key: "custom",
    methodology: "custom-curve",
    universeLabel: "User-Defined Curve",
    securityLabel: "investments",
    curveLabel: "user-defined",
    methodologyNote: "Assumes hypothetical investments are available with maturities matching each required cash flow date and yields determined by interpolation of the user-defined yield curve.",
    resultKey: "customCurve",
    nodes: Object.freeze([]),
    securityIds: Object.freeze([]),
  }),
});

export function normalizeHypotheticalPortfolioResult(result, universe = null) {
  if (!result) return null;
  const inferredUniverse =
    universe ||
    result.universe ||
    (result.methodology === "us-agencies" ? "agency" : "ust");
  const config = HYPOTHETICAL_CURVE_UNIVERSES[inferredUniverse];
  if (!config) return result;
  return {
    universe: config.key,
    universeLabel: config.universeLabel,
    securityLabel: config.securityLabel,
    curveLabel: config.curveLabel,
    methodology: config.methodology,
    methodologyLabel: `Hypothetical ${config.universeLabel} Portfolio`,
    methodologyNote: config.methodologyNote,
    isHypothetical: true,
    ...result,
  };
}

function isoDate(value) {
  const raw = String(value ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const parsed = dayjs(raw);
  return parsed.isValid() && parsed.format("YYYY-MM-DD") === raw ? raw : null;
}

function yieldFromRateRow(row) {
  const raw = row?.ratePct ?? row?.value;
  if (raw == null || String(raw).trim() === "") return null;
  const value = Number(String(raw).replace("%", ""));
  return Number.isFinite(value) ? value : NaN;
}

export function buildDatedHypotheticalCurve(asOfDate, rates = [], universe = "ust") {
  const config = HYPOTHETICAL_CURVE_UNIVERSES[universe];
  if (!config) throw new Error(`Unsupported hypothetical curve universe: ${universe}.`);
  const valuationDate = isoDate(asOfDate);
  if (!valuationDate) throw new Error(`A valid ${config.universeLabel} valuation date is required.`);

  const observations = new Map();
  const errors = [];
  for (const row of Array.isArray(rates) ? rates : []) {
    const securityId = String(row?.securityId ?? row?.security_id ?? row?.code ?? "")
      .trim()
      .toLowerCase();
    if (!config.securityIds.includes(securityId)) continue;
    if (observations.has(securityId)) {
      errors.push(`Duplicate ${config.curveLabel} yield observation for ${securityId}.`);
      continue;
    }
    const yieldPct = yieldFromRateRow(row);
    if (Number.isNaN(yieldPct) || (yieldPct != null && yieldPct < 0)) {
      errors.push(`${config.curveLabel} yield ${securityId} must be numeric and non-negative.`);
    }
    observations.set(securityId, yieldPct);
  }
  if (errors.length) throw new Error(errors.join("\n"));

  const base = dayjs(valuationDate).startOf("day");
  return config.nodes.map((definition) => {
    const date = base.add(definition.amount, definition.unit).format("YYYY-MM-DD");
    return {
      ...definition,
      curveLabel: config.curveLabel,
      valuationDate,
      date,
      elapsedDays: daysBetween(valuationDate, date),
      yieldPct: observations.has(definition.securityId)
        ? observations.get(definition.securityId)
        : null,
    };
  });
}

export function buildDatedUsTreasuryCurve(asOfDate, rates = []) {
  return buildDatedHypotheticalCurve(asOfDate, rates, "ust");
}

export function buildDatedUsAgencyCurve(asOfDate, rates = []) {
  return buildDatedHypotheticalCurve(asOfDate, rates, "agency");
}

export function buildDatedCustomCurve(asOfDate, rows = []) {
  const valuationDate = isoDate(asOfDate);
  if (!valuationDate) throw new Error("A valid User-Defined Curve valuation date is required.");

  const errors = [];
  const seenDates = new Set();
  const nodes = (Array.isArray(rows) ? rows : [])
    .filter((row) => String(row?.date ?? "").trim() || String(row?.yieldPct ?? row?.yield ?? "").trim())
    .map((row, index) => {
      const date = isoDate(row?.date);
      const rawYield = row?.yieldPct ?? row?.yield;
      const yieldPct = rawYield == null || String(rawYield).trim() === ""
        ? NaN
        : Number(String(rawYield).replace("%", ""));
      if (!date) errors.push(`User-Defined Curve row ${index + 1} has an invalid date.`);
      else if (!dayjs(date).isAfter(valuationDate, "day")) {
        errors.push(`User-Defined Curve row ${index + 1} must occur after ${valuationDate}.`);
      } else if (seenDates.has(date)) {
        errors.push(`User-Defined Curve contains duplicate date ${date}.`);
      } else {
        seenDates.add(date);
      }
      if (!Number.isFinite(yieldPct) || yieldPct <= -200) {
        errors.push(`User-Defined Curve row ${index + 1} must have a numeric yield greater than -200%.`);
      }
      return {
        securityId: date ? `custom-${date}` : `custom-row-${index + 1}`,
        tenor: date || `Row ${index + 1}`,
        curveLabel: "User-Defined Curve",
        valuationDate,
        date,
        elapsedDays: date ? daysBetween(valuationDate, date) : null,
        yieldPct,
        inputType: "yield",
      };
    });

  if (nodes.length < 2) errors.push("User-Defined Curve requires at least two dated yield points.");
  if (errors.length) throw new Error(errors.join("\n"));
  return nodes.sort((a, b) => a.elapsedDays - b.elapsedDays);
}

function missingNodeError(nodes) {
  const ids = [...new Set(nodes.map((node) => node.securityId))];
  return `Missing required ${nodes[0]?.curveLabel || "curve"} yield ${ids.join(" and ")}.`;
}

export function interpolateHypotheticalYield(curve, cashFlowDate) {
  const nodes = Array.isArray(curve) ? curve : [];
  if (!nodes.length) throw new Error("The hypothetical yield curve is empty.");
  const date = isoDate(cashFlowDate);
  if (!date) throw new Error(`Invalid  date: ${cashFlowDate || "(blank)"}.`);

  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  const targetDays = daysBetween(first.valuationDate, date);

  if (dayjs(date).isAfter(last.date, "day")) {
    throw new Error(
      `Cash-flow date ${date} is later than the supported ${last.securityId} node date ${last.date}.`
    );
  }

  const exact = nodes.find((node) => node.date === date);
  if (exact) {
    if (exact.yieldPct == null) throw new Error(missingNodeError([exact]));
    return {
      lowerNode: exact,
      upperNode: exact,
      interpolationWeight: 0,
      interpolatedYieldPct: exact.yieldPct,
      boundaryRule: "exact-node",
    };
  }

  if (dayjs(date).isBefore(first.date, "day")) {
    if (first.yieldPct == null) throw new Error(missingNodeError([first]));
    return {
      lowerNode: first,
      upperNode: first,
      interpolationWeight: 0,
      interpolatedYieldPct: first.yieldPct,
      boundaryRule: "flat-before-first-node",
    };
  }

  const upperIndex = nodes.findIndex((node) => dayjs(node.date).isAfter(date, "day"));
  const lower = nodes[upperIndex - 1];
  const upper = nodes[upperIndex];
  const missing = [lower, upper].filter((node) => node?.yieldPct == null);
  if (missing.length) throw new Error(missingNodeError(missing));

  const spanDays = upper.elapsedDays - lower.elapsedDays;
  const weight = (targetDays - lower.elapsedDays) / spanDays;
  return {
    lowerNode: lower,
    upperNode: upper,
    interpolationWeight: weight,
    interpolatedYieldPct: lower.yieldPct + weight * (upper.yieldPct - lower.yieldPct),
    boundaryRule: "interpolated",
  };
}

export function interpolateUsTreasuryYield(curve, cashFlowDate) {
  return interpolateHypotheticalYield(curve, cashFlowDate);
}

export function usTreasuryDiscountFactor(yieldPct, yearFraction) {
  const decimalYield = Number(yieldPct) / 100;
  return Math.pow(1 + decimalYield / 2, -2 * Number(yearFraction));
}

export function solveUsTreasuryPortfolioXirr(rows, portfolioCost) {
  const cashFlows = Array.isArray(rows) ? rows : [];
  const target = Number(portfolioCost);
  if (!cashFlows.length || !Number.isFinite(target) || target <= 0) return null;

  const presentValueAt = (annualEffectiveRate) =>
    cashFlows.reduce(
      (sum, row) =>
        sum +
        Number(row.grossCashFlow) *
          Math.pow(1 + annualEffectiveRate, -Number(row.yearFraction)),
      0
    );

  let low = 0;
  let high = 1;
  if (presentValueAt(low) < target) return null;
  while (presentValueAt(high) > target && high < 1024) high *= 2;
  if (presentValueAt(high) > target) return null;

  for (let i = 0; i < 200; i += 1) {
    const mid = (low + high) / 2;
    if (presentValueAt(mid) > target) low = mid;
    else high = mid;
  }
  return ((low + high) / 2) * 100;
}

export function calculateHypotheticalPortfolio({
  universe = "ust",
  asOfDate,
  settlementDate,
  cashFlows = [],
  rates = [],
} = {}) {
  const config = HYPOTHETICAL_CURVE_UNIVERSES[universe];
  if (!config) throw new Error(`Unsupported hypothetical curve universe: ${universe}.`);
  const curveAsOfDate = isoDate(asOfDate);
  const valuationDate = isoDate(settlementDate || asOfDate);
  if (!valuationDate) throw new Error(`A valid ${config.universeLabel} valuation date is required.`);
  if (universe !== "custom" && !curveAsOfDate) {
    throw new Error(`A valid ${config.universeLabel} market as-of date is required.`);
  }
  if (!Array.isArray(cashFlows) || !cashFlows.length) {
    throw new Error("At least one required cash flow is required.");
  }

  const validationErrors = [];
  const normalized = cashFlows.map((flow, index) => {
    const date = isoDate(flow?.date ?? flow?.drawDate);
    const amount = Number(flow?.amount ?? flow?.drawRequirement);
    if (!date) validationErrors.push(`Cash flow ${index + 1} has an invalid date.`);
    else if (!dayjs(date).isAfter(valuationDate, "day")) {
      validationErrors.push(`Cash flow ${index + 1} must occur after ${valuationDate}.`);
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      validationErrors.push(`Cash flow ${index + 1} must have a positive amount.`);
    }
    return { id: flow?.id ?? `${universe}-cash-flow-${index + 1}`, date, amount, index };
  });
  if (validationErrors.length) throw new Error(validationErrors.join("\n"));

  const curve = universe === "custom"
    ? buildDatedCustomCurve(valuationDate, rates)
    : buildDatedHypotheticalCurve(curveAsOfDate, rates, universe);
  const calculationErrors = [];
  const rows = normalized.map((flow) => {
    try {
      const interpolation = interpolateHypotheticalYield(curve, flow.date);
      const actualDays = daysBetween(valuationDate, flow.date);
      const yearFraction = dayCountFraction(valuationDate, flow.date, "act/365");
      const discountFactor = usTreasuryDiscountFactor(
        interpolation.interpolatedYieldPct,
        yearFraction
      );
      const presentValue = flow.amount * discountFactor;
      return {
        id: flow.id,
        cashFlowDate: flow.date,
        grossCashFlow: flow.amount,
        hypotheticalMaturityAmount: flow.amount,
        lowerTenor: interpolation.lowerNode.tenor,
        lowerSecurityId: interpolation.lowerNode.securityId,
        lowerNodeDate: interpolation.lowerNode.date,
        lowerYieldPct: interpolation.lowerNode.yieldPct,
        upperTenor: interpolation.upperNode.tenor,
        upperSecurityId: interpolation.upperNode.securityId,
        upperNodeDate: interpolation.upperNode.date,
        upperYieldPct: interpolation.upperNode.yieldPct,
        interpolationWeight: interpolation.interpolationWeight,
        interpolatedYieldPct: interpolation.interpolatedYieldPct,
        boundaryRule: interpolation.boundaryRule,
        actualDays,
        yearFraction,
        discountFactor,
        presentValue,
      };
    } catch (error) {
      calculationErrors.push(`Cash flow ${flow.index + 1} (${flow.date}): ${error.message}`);
      return null;
    }
  });
  if (calculationErrors.length) throw new Error(calculationErrors.join("\n"));

  const totalGrossCashFlows = rows.reduce((sum, row) => sum + row.grossCashFlow, 0);
  const portfolioCost = rows.reduce((sum, row) => sum + row.presentValue, 0);
  const weightedAverageLife = rows.reduce(
    (sum, row) => sum + row.grossCashFlow * row.yearFraction,
    0
  ) / totalGrossCashFlows;
  const weightedAverageInterpolatedYieldPct = rows.reduce(
    (sum, row) => sum + row.grossCashFlow * row.interpolatedYieldPct,
    0
  ) / totalGrossCashFlows;
  const dates = rows.map((row) => row.cashFlowDate).sort();
  return {
    universe,
    universeLabel: config.universeLabel,
    securityLabel: config.securityLabel,
    curveLabel: config.curveLabel,
    methodology: config.methodology,
    methodologyLabel: `Hypothetical ${config.universeLabel} Portfolio`,
    methodologyNote: config.methodologyNote,
    interpolationMethod: "linear",
    valuationDate,
    curveAsOfDate: universe === "custom" ? null : curveAsOfDate,
    yearFractionBasis: "act/365",
    compounding: "nominal-semiannual",
    isHypothetical: true,
    curveInputs:
      universe === "custom"
        ? curve.map((node) => ({ date: node.date, yieldPct: node.yieldPct }))
        : null,
    rows,
    summary: {
      cashFlowCount: rows.length,
      totalGrossCashFlows,
      totalMaturityAmount: totalGrossCashFlows,
      portfolioCost,
      totalDiscount: totalGrossCashFlows - portfolioCost,
      costPercentage: (portfolioCost / totalGrossCashFlows) * 100,
      earliestCashFlowDate: dates[0] || null,
      latestCashFlowDate: dates[dates.length - 1] || null,
      weightedAverageLife,
      weightedAverageInterpolatedYieldPct,
      portfolioXirrPct: solveUsTreasuryPortfolioXirr(rows, portfolioCost),
    },
  };
}

export function calculateUsTreasuryPortfolio(options = {}) {
  return calculateHypotheticalPortfolio({ ...options, universe: "ust" });
}

export function calculateUsAgencyPortfolio(options = {}) {
  return calculateHypotheticalPortfolio({ ...options, universe: "agency" });
}
